import { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

// FAQ items data
const faqItems = [
  {
    question: "What types of issues do sex therapists address?",
    answer:
      "Sex therapy covers a lot of ground. People come in for things like low desire, painful sex, performance worries, mismatched libidos, questions about orientation or gender, and relationship issues that spill into the bedroom. Sessions can be for you alone or with your partner, and you always keep your clothes on. It's talk therapy.",
  },
  {
    question: "How do I get started?",
    answer:
      "Call, text, or send the consultation form, whichever feels easiest. Every message goes straight to Dr. Rothenberg, and she'll set up a free 15 minute phone consultation. It's a low-pressure way to share what's going on, ask questions, and see if working together feels like a good fit.",
  },
  {
    question: "Does Dr. Rothenberg offer virtual counseling sessions?",
    answer:
      "Yes. Many clients meet with Dr. Rothenberg by video, whether that's for convenience, privacy, or distance. Virtual sessions work just as well as in-person ones for most concerns.",
  },
  {
    question: "What insurance plans does Dr. Rothenberg accept?",
    answer:
      "Dr. Rothenberg is in-network with Aetna, Cigna, Oscar, Oxford, and United Health Care insurance plans.",
  },
  {
    question: "Are virtual sessions covered by insurance?",
    answer:
      "Usually, yes. Most plans cover virtual sessions the same way they cover in-person visits, but it's worth a quick call to your insurance company to confirm before your first appointment.",
  },
];

function FAQSection() {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(251,241,239,0.3) 100%)",
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: "absolute",
          left: { xs: "-30%", md: "-15%" },
          top: "10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(228,181,162,0.1) 0%, rgba(255,255,255,0) 70%)",
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          right: { xs: "-30%", md: "-10%" },
          bottom: "5%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,181,138,0.1) 0%, rgba(255,255,255,0) 70%)",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={4}>
          {/* Left side - Title and Description */}
          <Grid
            size={{ xs: 12, md: 4 }}
            sx={{
              mb: { xs: 2, md: 0 },
              textAlign: { xs: "center", md: "left" }, // Center on mobile, left on desktop
            }}
          >
            <Box
              sx={{
                position: { xs: "static", md: "sticky" },
                top: { md: 100 },
                padding: { xs: 2, md: 4 },
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                Frequently Asked Questions
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "#666",
                  fontSize: { xs: "1rem", md: "1.125rem" },
                }}
              >
                A few things people often want to know before reaching out.
              </Typography>
            </Box>
          </Grid>

          {/* Right side - Accordion */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Box>
              {faqItems.map((item, index) => (
                <Accordion
                  key={index}
                  expanded={expanded === `panel${index}`}
                  onChange={handleChange(`panel${index}`)}
                  elevation={0}
                  disableGutters
                  sx={{
                    mb: 2,
                    border: "1px solid",
                    borderColor:
                      expanded === `panel${index}`
                        ? "rgba(228, 181, 162, 0.5)"
                        : "rgba(0, 0, 0, 0.06)",
                    borderRadius: "12px !important",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&::before": {
                      display: "none", // Remove the default divider
                    },
                    "&:hover": {
                      borderColor: "rgba(228, 181, 162, 0.5)",
                      boxShadow:
                        expanded === `panel${index}`
                          ? "0 6px 20px rgba(228, 181, 162, 0.15)"
                          : "none",
                    },
                    backgroundColor:
                      expanded === `panel${index}`
                        ? "rgba(251, 241, 239, 0.7)"
                        : "white",
                    boxShadow:
                      expanded === `panel${index}`
                        ? "0 6px 20px rgba(228, 181, 162, 0.15)"
                        : "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      expanded === `panel${index}` ? (
                        <RemoveIcon sx={{ color: "secondary.main" }} />
                      ) : (
                        <AddIcon sx={{ color: "#666" }} />
                      )
                    }
                    aria-controls={`panel${index}a-content`}
                    id={`panel${index}a-header`}
                    sx={{
                      padding: { xs: "12px 16px", md: "20px" }, // Less padding on mobile
                      "&:hover": {
                        backgroundColor: "rgba(251, 241, 239, 0.5)",
                      },
                      "& .MuiAccordionSummary-content": {
                        margin: "12px 0", // Fixed height to prevent title from moving
                        justifyContent: { xs: "flex-start", md: "flex-start" }, // Left-align on mobile
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "1rem", md: "1.125rem" },
                        fontWeight: 500, // Consistent weight whether open or closed
                        color:
                          expanded === `panel${index}`
                            ? "secondary.main"
                            : "text.primary",
                        transition: "color 0.3s ease", // Only transition the color, not other properties
                        textAlign: "left", // Always left-align the text
                      }}
                    >
                      {item.question}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      padding: { xs: "0 16px 16px", md: "0 24px 24px" }, // Less padding on mobile
                      borderTop: "1px solid rgba(228, 181, 162, 0.2)",
                      textAlign: "left", // Left-align the answer text
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "0.95rem", md: "1rem" },
                        color: "text.secondary",
                        lineHeight: 1.6,
                        mt: 2,
                      }}
                    >
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default FAQSection;
